import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Calendar, FileText, Users, Download, Search, Filter,
  ChevronDown, LogOut, Shield, Clock, CheckCircle, XCircle, Loader2, Trash2
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  confirmed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  completed: "bg-green-500/20 text-green-400 border-green-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
};

const STATUS_ICONS = {
  pending: Clock,
  confirmed: CheckCircle,
  completed: CheckCircle,
  cancelled: XCircle,
};

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-[#14181C]/70 border border-[#333333] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-[#B0B0B0]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</span>
        <Icon className="w-4 h-4 text-[#38b6ff]" />
      </div>
      <p className="text-2xl font-bold text-[#F5F5F7]" style={{ fontFamily: 'Unbounded, sans-serif' }}>{value}</p>
    </div>
  );
}

function BookingRow({ booking, onStatusChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const StatusIcon = STATUS_ICONS[booking.status] || Clock;

  return (
    <div className="border border-[#333333] bg-[#14181C]/50 mb-2" data-testid={`booking-${booking.id}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <FileText className="w-4 h-4 text-[#38b6ff] flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#F5F5F7] truncate">{booking.name}</p>
            <p className="text-xs text-[#B0B0B0]">{booking.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-[#B0B0B0] hidden md:block">{booking.date} {booking.time}</span>
          <span className={`text-xs px-2 py-1 border rounded ${STATUS_COLORS[booking.status]}`}>
            <StatusIcon className="w-3 h-3 inline mr-1" />{booking.status}
          </span>
          <ChevronDown className={`w-4 h-4 text-[#B0B0B0] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-[#333333] pt-3 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><span className="text-[#B0B0B0]">Phone:</span> <span className="text-[#F5F5F7]">{booking.phone}</span></div>
            <div><span className="text-[#B0B0B0]">Services:</span> <span className="text-[#F5F5F7]">{booking.services}</span></div>
            <div><span className="text-[#B0B0B0]">Proceeding:</span> <span className="text-[#F5F5F7]">{booking.proceeding_type}</span></div>
            <div><span className="text-[#B0B0B0]">Estimate:</span> <span className="text-[#F5F5F7]">{booking.time_estimate}</span></div>
          </div>
          {booking.notes && <p className="text-xs text-[#B0B0B0]"><span className="text-[#E0E0E0]">Notes:</span> {booking.notes}</p>}
          {booking.nod_filename && <p className="text-xs text-[#B0B0B0]">NOD: <span className="text-[#38b6ff]">{booking.nod_filename}</span> ({Math.round((booking.nod_file_size || 0) / 1024)}KB)</p>}
          <div className="flex items-center gap-2 pt-2">
            {["pending", "confirmed", "completed", "cancelled"].map(s => (
              <button
                key={s}
                onClick={() => onStatusChange(booking.id, s)}
                disabled={booking.status === s}
                className={`text-xs px-3 py-1 border rounded transition-all ${
                  booking.status === s ? "opacity-40 cursor-not-allowed" : "hover:opacity-80 cursor-pointer"
                } ${STATUS_COLORS[s]}`}
                data-testid={`status-btn-${s}-${booking.id}`}
              >
                {s}
              </button>
            ))}
            <button
              onClick={() => { if (window.confirm("Delete this booking?")) onDelete(booking.id); }}
              className="text-xs px-3 py-1 border rounded border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all cursor-pointer ml-auto"
              data-testid={`delete-booking-${booking.id}`}
            >
              <Trash2 className="w-3 h-3 inline mr-1" />Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadRow({ lead, onDelete }) {
  return (
    <div className="border border-[#333333] bg-[#14181C]/50 p-4 mb-2" data-testid={`lead-${lead.id}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[#F5F5F7]">{lead.name}</p>
          <p className="text-xs text-[#B0B0B0]">{lead.email}{lead.phone ? ` | ${lead.phone}` : ""}</p>
          {lead.message && <p className="text-xs text-[#E0E0E0] mt-1 line-clamp-2">{lead.message}</p>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <span className="text-xs text-[#B0B0B0]">{new Date(lead.created_at).toLocaleDateString()}</span>
          <button
            onClick={() => { if (window.confirm("Delete this lead?")) onDelete(lead.id); }}
            className="text-xs p-1.5 border rounded border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all cursor-pointer"
            data-testid={`delete-lead-${lead.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        if (res.data.role !== "admin") {
          toast.error("Admin access required");
          navigate("/login");
        } else {
          setUser(res.data);
        }
      })
      .catch(() => {
        toast.error("Please login as admin");
        navigate("/login");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const fetchData = useCallback(() => {
    if (!user) return;
    axios.get(`${API}/bookings`, { withCredentials: true }).then(r => setBookings(r.data)).catch(() => {});
    axios.get(`${API}/leads`, { withCredentials: true }).then(r => setLeads(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status: newStatus }, { withCredentials: true });
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
      toast.success(`Booking updated to ${newStatus}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { withCredentials: true });
      setBookings(prev => prev.filter(b => b.id !== bookingId));
      toast.success("Booking deleted");
    } catch {
      toast.error("Failed to delete booking");
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`, { withCredentials: true });
      setLeads(prev => prev.filter(l => l.id !== leadId));
      toast.success("Lead deleted");
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/login");
  };

  const exportCSV = () => {
    const data = tab === "bookings" ? bookings : leads;
    if (!data.length) return toast.error("No data to export");
    const headers = Object.keys(data[0]);
    const csv = [headers.join(","), ...data.map(row => headers.map(h => `"${String(row[h] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tab}_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const filteredBookings = bookings.filter(b => {
    const matchesSearch = !search || [b.name, b.email, b.services].some(f => (f || "").toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = statusFilter === "all" || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredLeads = leads.filter(l =>
    !search || [l.name, l.email, l.message].some(f => (f || "").toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center pt-20">
      <Loader2 className="w-8 h-8 text-[#38b6ff] animate-spin" />
    </div>
  );

  if (!user) return null;

  const pendingCount = bookings.filter(b => b.status === "pending").length;
  const confirmedCount = bookings.filter(b => b.status === "confirmed").length;

  return (
    <div className="bg-[#0D0D0D] min-h-screen pt-20 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-[#38b6ff]" />
              <p className="text-xs uppercase tracking-wider text-[#38b6ff]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Admin Dashboard</p>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#F5F5F7]" style={{ fontFamily: 'Unbounded, sans-serif' }} data-testid="admin-heading">
              Dashboard
            </h1>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-[#333333] text-[#E0E0E0] hover:bg-[#333333] hover:text-[#F5F5F7]" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Bookings" value={bookings.length} icon={FileText} />
          <StatCard label="Pending" value={pendingCount} icon={Clock} />
          <StatCard label="Confirmed" value={confirmedCount} icon={CheckCircle} />
          <StatCard label="Leads" value={leads.length} icon={Users} />
        </div>

        {/* Tabs + Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-[#14181C] border border-[#333333] p-1">
            {[{ id: "bookings", label: "Bookings", icon: FileText }, { id: "leads", label: "Leads", icon: Users }].map(t => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setSearch(""); setStatusFilter("all"); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition-all ${
                  tab === t.id ? "bg-[#38b6ff]/10 text-[#38b6ff] border border-[#38b6ff]/30" : "text-[#B0B0B0] hover:text-[#F5F5F7]"
                }`}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B0B0]" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 bg-[#14181C] border-[#333333] text-[#F5F5F7] placeholder:text-[#B0B0B0]/50 focus:border-[#38b6ff] sm:w-60"
                data-testid="admin-search"
              />
            </div>
            {tab === "bookings" && (
              <div className="relative">
                <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B0B0]" />
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="pl-9 pr-3 py-2 bg-[#14181C] border border-[#333333] text-[#F5F5F7] text-sm appearance-none cursor-pointer focus:border-[#38b6ff] outline-none"
                  data-testid="admin-status-filter"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            )}
            <Button onClick={exportCSV} variant="outline" className="border-[#333333] text-[#E0E0E0] hover:bg-[#333333] hover:text-[#F5F5F7]" data-testid="admin-export-csv">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
            <Button onClick={fetchData} variant="outline" className="border-[#333333] text-[#E0E0E0] hover:bg-[#333333] hover:text-[#F5F5F7]" data-testid="admin-refresh">
              Refresh
            </Button>
          </div>
        </div>

        {/* Content */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={tab}>
          {tab === "bookings" ? (
            filteredBookings.length ? filteredBookings.map(b => (
              <BookingRow key={b.id} booking={b} onStatusChange={handleStatusChange} onDelete={handleDeleteBooking} />
            )) : (
              <div className="text-center py-16 text-[#B0B0B0]">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No bookings found</p>
              </div>
            )
          ) : (
            filteredLeads.length ? filteredLeads.map(l => (
              <LeadRow key={l.id} lead={l} onDelete={handleDeleteLead} />
            )) : (
              <div className="text-center py-16 text-[#B0B0B0]">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No leads found</p>
              </div>
            )
          )}
        </motion.div>
      </div>
    </div>
  );
}
